import {
  Flex,
  View,
  Heading,
  Text,
  Button,
  Card,
} from "@aws-amplify/ui-react";
import { GraphQLResult } from "@aws-amplify/api-graphql";
import * as queries from "../../graphql/queries";
import moment from "moment";
// import "../styles/app.css";
import "./Home.css";
import { useNavigate } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";
import {
  PushNotificationType,
  Schedule,
  TableData,
} from "../_shared/pushNotifications/types";
import {
  useGetAppConfigQuery,
  useGetMessagesQuery,
  useGetPushNotificationQuery,
} from "../../graphql/generated/graphql";
import { sortTableDataByDateTime } from "../_shared/utils";
import { createTableRowItem } from "../PushNotification/helper";
import { Row } from "react-bootstrap";
import { API, graphqlOperation } from "aws-amplify";
import { getPolicyType } from "../PrivacyPolicy/PrivacyPolicType";
import { formatDisplayName } from "../_shared/helper";

export const Home = () => {
  const navigate = useNavigate();
  const push = useGetPushNotificationQuery();
  const msg = useGetMessagesQuery();
  const appConfig = useGetAppConfigQuery();

  const now = Date.now();

  const [username, setUsername] = useState("");
  const [activeMessages, setActiveMessages] = useState(0);
  const [upcomingNotification, setUpcomingNotification] = useState(0);
  const [activePolicies, setActivePolicies] = useState(0);
  const [appVersion, setAppVersion] = useState("0.0.0");
  const [futureMessages, setFutureMessages] = useState([]);
  const [futureNotifications, setFutureNotifications] = useState([]);

  const fetchMessages = useCallback(async () => {
    try {
      if (msg.loading || msg.error || !msg.data) throw msg.error;
      const filteredMessages = msg.data?.getMessages?.map((message) => {
        return {
          ...message,
          Active: message.Active,
        };
      });
      const activeMsg = filteredMessages.filter((item) => {
        return item.Active;
      });
      setActiveMessages(activeMsg.length);

      const futureMsgs = filteredMessages.filter((item) => {
        return item && item.Active && new Date(item.StartDate).getTime() > now;
      });

      setFutureMessages(futureMsgs);
    } catch (e) {
      console.log("Error fetching messages:", e);
    }
  }, [msg.data, msg.error, msg.loading, now]);

  const fetchNotifications = useCallback(async () => {
    if (push.loading || push.error || !push.data) return;
    const { allSchedules, pushNotification } = push.data.getPushNotification;

    if (!allSchedules && !pushNotification) {
      console.log("401 Unauthorized error: Token UUID not found");
      throw new Error(
        "Sorry, something went wrong with fetching push notifications"
      );
    }

    const schedules: Schedule[] = allSchedules.schedules ?? [];
    const pushNotifcation: PushNotificationType[] = pushNotification ?? [];

    const getPushNotificationDbData = (
      scheduleId: number
    ): PushNotificationType =>
      pushNotifcation.find((i) => i.scheduleId === scheduleId);

    const createLists = schedules.reduce(
      (
        acc: { tableList: TableData[]; scheduleList: Schedule[] },
        schedule: Schedule
      ) => {
        const scheduleDb: PushNotificationType = getPushNotificationDbData(
          schedule.scheduleId
        );

        if (scheduleDb !== undefined) {
          acc.tableList.push(createTableRowItem(scheduleDb, schedule));
          acc.scheduleList.push(schedule);
        }
        return acc;
      },
      {
        tableList: [],
        scheduleList: [],
      }
    );
    const filteredData = sortTableDataByDateTime(createLists.tableList);
    const upcoming = filteredData.filter((item) => {
      return item && new Date(item.SendDate).getTime() > now;
    });
    setUpcomingNotification(upcoming.length);

    const futurePush = filteredData.filter((item) => {
      return item && new Date(item.SendDate).getTime() > now;
    });
    setFutureNotifications(futurePush);
  }, [now, push.data, push.error, push.loading]);

  const fetchPolicies = useCallback(async () => {
    try {
      const policies = (await API.graphql(
        graphqlOperation(queries.getPolicy)
      )) as GraphQLResult<getPolicyType>;
      const filteredPolicies = policies?.data?.getPolicy?.map((policy) => {
        return {
          Active: policy.isActive,
        };
      });
      const activePolicies = filteredPolicies.filter((item) => {
        return item.Active;
      });
      setActivePolicies(activePolicies.length);
    } catch (e) {
      console.error("Error fetching policies:", e);
    }
  }, []);

  const fetchAppConfig = useCallback(async () => {
    try {
      const items = appConfig?.data.getAppConfig;
      if (items.length > 0) {
        setAppVersion(items[0].minimumAppVersion);
      }
    } catch (e) {
      console.log("Error fetching config:", e);
    }
  }, [appConfig]);

  useEffect(() => {
    setUsername(localStorage.getItem("USER_NAME"));
    fetchMessages();
    fetchNotifications();
    fetchPolicies();
    fetchAppConfig();
  }, [msg, push, appConfig, fetchMessages, fetchNotifications, fetchPolicies, fetchAppConfig]);

  const onClick = (type: string) => {
    switch (type) {
      case "message":
        navigate("/new-message");
        break;
      case "notification":
        navigate("/new-notification");
        break;
      case "policy":
        navigate("/add-policy");
        break;
      case "config":
        navigate("/featureflags");
        break;
      default:
        console.log("./home");
    }
  };

  return (
    <View className="dashboard">
      <Flex
        className="dash-top"
        justifyContent="space-between"
        alignItems="center"
      >
        <Heading level={2} className="screen-title">
          Welcome, {formatDisplayName(username)}
        </Heading>
        {/* <Flex gap="0.5rem" className="dash-badges">
          <Badge variation="info">{DEPLOY_ENVIRONMENT}</Badge>
          <Badge variation="info">{"v " + APP_VERSION}</Badge>
        </Flex> */}
      </Flex>
      <p className="sub-text">Here's what's going on today</p>

      <p className="heading-text">Quick Links</p>
      <Flex wrap="wrap" gap="0.75rem" className="dash-actions">
        <Button
          variation="primary"
          className="cta"
          onClick={() => onClick("message")}
        >
          New Message
        </Button>
        <Button
          variation="primary"
          className="cta"
          onClick={() => onClick("notification")}
        >
          New Notification
        </Button>
        <Button
          variation="primary"
          className="cta"
          onClick={() => onClick("policy")}
        >
          New policy
        </Button>
        <Button className="action-ghost" onClick={() => onClick("config")}>
          Update Config
        </Button>
      </Flex>

      <p className="heading-text2">A Quick Glance</p>
      <Flex gap="1rem" wrap="wrap" className="infos-row">
        <Card className="infos">
          <Text className="infos-label">Active Messages</Text>
          <Heading level={3} className="infos-value">
            {activeMessages}
          </Heading>
        </Card>
        <Card className="infos">
          <Text className="infos-label">Scheduled Notifications</Text>
          <Heading level={3} className="infos-value">
            {upcomingNotification}
          </Heading>
        </Card>
        <Card className="infos">
          <Text className="infos-label">Active Policies</Text>
          <Heading level={3} className="infos-value">
            {activePolicies}
          </Heading>
        </Card>
        <Card className="infos">
          <Text className="infos-label">Current Minimum App Version</Text>
          <Heading level={3} className="infos-value">
            {appVersion}
          </Heading>
        </Card>
      </Flex>

      <Flex as={Row} gap="1rem" wrap="wrap" className="panels">
        <Card className="panel" width={{ base: "100%", large: "58%" }}>
          <Flex
            className="panel-head"
            justifyContent="space-between"
            alignItems="center"
          >
            <Heading level={5}>Upcoming messages</Heading>
          </Flex>

          {futureMessages.length === 0 ? (
            <Text className="muted">Nothing scheduled.</Text>
          ) : (
            <View as="ul" className="list">
              {futureMessages.slice(0, 8).map((item) => (
                <li key={item.id} className="row">
                  <div className="title">{item.Title}</div>
                  <div className="when">
                    {moment(item.StartDate).format("YYYY-MM-DD HH:mm")}
                  </div>
                </li>
              ))}
            </View>
          )}
        </Card>

        <Card className="panel" width={{ base: "100%", large: "58%" }}>
          <Flex
            className="panel-head"
            justifyContent="space-between"
            alignItems="center"
          >
            <Heading level={5}>Upcoming push notifications</Heading>
          </Flex>

          {futureNotifications.length === 0 ? (
            <Text className="muted">Nothing scheduled.</Text>
          ) : (
            <View as="ul" className="list">
              {futureNotifications.slice(0, 8).map((item) => (
                <li key={item.scheduleId} className="row">
                  <div className="title">{item.Title}</div>
                  <div className="col when">
                    {moment(item.SendDate).format("YYYY-MM-DD HH:mm")}
                  </div>
                </li>
              ))}
            </View>
          )}
        </Card>
      </Flex>
    </View>
  );
};
